import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { ADVISORY, DEFECTS, HYGIENE, auditSet, repairRuleFor } from '../../lib/championshipAudit.js'
import { decidedBy } from '../../lib/decidedBy.js'
import { api } from '../../lib/api.js'
/* The repair runs through the shared runner so its sentence carries the cap
   it actually applied — see the note at the call site. */
import { repairDefect } from '../command-center/bodyControls.js'
import ChampionshipDrawer from '../command-center/ChampionshipDrawer.jsx'
import { normalizeChampionship } from '../../lib/championships.js'
import { championshipsFor } from '../../lib/bodyChampionships.js'
import styles from './DefectPanel.module.css'

/* WHICH DEFECTS, AND ON WHICH RECORDS.
 *
 * Brian: "there seem like a ton of DEFECTS in the data in governing bodies. it
 * doesnt allow me to see which defects there are to fix them."
 *
 * ⚠️ THE PAGE HAD THE NUMBER AND NO DOOR. `championshipAudit.js` has exported
 * `defectiveRecords()` — the actual offending records, per state, per defect —
 * for a while. The Governing Bodies card rendered `held.issues` as a bare
 * figure and never called it. So the defect count was a report of a problem
 * with no way to reach the problem, which is the same "shipped and unreachable
 * is the same as absent" failure that once hid the college rosters three
 * clicks deep.
 *
 * A number you cannot open is a number you cannot act on.
 *
 * This is the door. It is READ-ONLY on purpose: every defect here needs
 * judgement — which of two duplicates is the good one, whether a blank date is
 * unknown or genuinely has no event — and auto-resolving them is how a bad
 * record becomes the surviving record.
 */
/* `rows`, when given, is the exact set to audit. The Governing Bodies page
 * passes it for the "(state missing)" bucket, which no body's matcher can
 * reach because those records name no state. */
export default function DefectPanel({ entity, universe, rows: givenRows = null, onClose }) {
  const [only, setOnly] = useState('')
  /* ⚠️ A DEFECT ROW MUST OPEN THE RECORD. Listing what is wrong and giving no
     way to reach the thing that is wrong is the same dead end as the count
     that started this — one level deeper. Tapping a row opens the full record
     in the same drawer the Command Center uses, so the fix happens where every
     other correction happens rather than in a second, divergent editor. */
  const [record, setRecord] = useState(null)
  /* ⚠️ DRY RUN FIRST, ALWAYS — and the apply button does not exist until a dry
     run has come back. Brian: "does this give us an option to fix the defects?"
     Yes, but a bulk write across a live object is the one thing that must never
     be one click from a list view. Automation proposes; a human sees exactly
     what would change and then decides. */
  const [fix, setFix] = useState({ busy: false, dry: null, applied: null, error: '', id: '' })

  /* ⚠️ SCOPED TO THE ENTITY, NOT TO A STATE. Brian: "i need to make sure we are
     doing this for all tabs."
     `defectiveRecords` buckets by STATE, which is why this only ever worked on
     the high-school tab — a conference has no state, so college, Canada and pro
     silently had no defect view at all. `championshipsFor` already resolves the
     records for ANY entity at any level, so the defects are computed from those
     rows directly and every tab gets the same door. */
  /* ⚠️ ONE AUDIT, THE CARD'S AND THIS DRAWER'S. `auditSet` is what the card
     badge counts too, so the badge and "All N" here cannot disagree again. */
  const { rows, counts, total, blocking, idsBy } = useMemo(() => {
    const mine = givenRows || championshipsFor(entity || {}, universe || [], { scope: 'body' })?.rows || []
    const audit = auditSet(mine)
    const bad = audit.items.filter((it) => it.ids.length)
    const idsBy = new Map()
    for (const it of bad) {
      const rid = String(it.raw.id || it.raw.hs_object_id || it.rec?.id || '')
      if (!rid) continue
      for (const id of it.ids) { if (!idsBy.has(id)) idsBy.set(id, []); idsBy.get(id).push(rid) }
    }
    /* "All" means the blocking set, the same number the card shows. Advisory
       and bookkeeping rows are reached through their own chips. */
    const filtered = only ? bad.filter((it) => it.ids.includes(only)) : bad.filter((it) => it.blocking)
    return {
      rows: filtered.slice(0, 400).map((it) => ({ raw: it.raw, ids: it.ids })),
      counts: audit.counts, total: filtered.length, blocking: audit.blocking, idsBy,
    }
  }, [entity, universe, givenRows, only])

  if (!entity) return null

  /* ⚠️ BLOCKING FIRST, HYGIENE LAST AND LABELLED.
     Brian: "the defects youve got shown dont really seem like a defect."
     They mostly were not. `noKey` alone was ~3,489 records — half of every
     "defect" reported — and it only affects idempotent bulk loading, nothing a
     person does with a championship. Mixing it in taught people to distrust the
     whole page. */
  const catalog = DEFECTS.filter((d) => counts.get(d.id) && !d.audience)
  const advisory = DEFECTS.filter((d) => counts.get(d.id) && d.audience === ADVISORY)
  const hygiene = DEFECTS.filter((d) => counts.get(d.id) && d.audience === HYGIENE)

  return createPortal(
    <div className={styles.scrim} role="dialog" aria-modal="true" onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <header className={styles.head}>
          <div>
            <p className={styles.eyebrow}>Record integrity</p>
            <h2>{entity.acronym} — what is actually wrong</h2>
            <p className={styles.sub}>
              {blocking.toLocaleString()} {blocking === 1 ? 'record has' : 'records have'} a blocking defect in {entity.acronym}.
              This is the same number as the card.
              {/* ⚠️ THIS SENTENCE USED TO READ "Read-only: every one of these needs a
                  human call, so nothing here edits the record." It stopped being true
                  the day the repair buttons shipped below it, and a panel that
                  describes itself wrongly is how somebody decides not to look for the
                  control that is already there. Corrected rather than deleted, so the
                  change is on the record. */}
              The rules that only ever fill a blank can be run from here, dry run first;
              the ones that need a judgement are listed below as needing one, and say why.
            </p>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </header>

        {/* Filter by defect. Each says what it COSTS, because a defect nobody
            understands the consequence of is one nobody prioritises. */}
        <div className={styles.chips}>
          <button type="button" data-on={only === '' ? '1' : '0'} onClick={() => setOnly('')}>
            All blocking <b>{blocking.toLocaleString()}</b>
          </button>
          {catalog.map((d) => (
            <button key={d.id} type="button" data-on={only === d.id ? '1' : '0'}
              title={d.cost} onClick={() => setOnly(d.id)}>
              {d.label} <b>{counts.get(d.id).toLocaleString()}</b>
            </button>
          ))}
        </div>

        {advisory.length > 0 && (
          <div className={styles.hygiene}>
            <span className={styles.hygieneHead}>Worth seeing, not counted as defects</span>
            {advisory.map((d) => (
              <button key={d.id} type="button" data-on={only === d.id ? '1' : '0'}
                title={d.cost} onClick={() => setOnly(d.id)}>
                {d.label} <b>{counts.get(d.id).toLocaleString()}</b>
              </button>
            ))}
            <p className={styles.hygieneWhy}>
              A projected-only date, a title decided by standings, a retired record, or a conference
              title that does not say which one it tracks. Real, and worth a look, but none of them
              stops anyone working the record, so none is in the count above.
            </p>
          </div>
        )}

        {hygiene.length > 0 && (
          <div className={styles.hygiene}>
            <span className={styles.hygieneHead}>Bookkeeping — not blocking anyone</span>
            {hygiene.map((d) => (
              <button key={d.id} type="button" data-on={only === d.id ? '1' : '0'}
                title={d.cost} onClick={() => setOnly(d.id)}>
                {d.label} <b>{counts.get(d.id).toLocaleString()}</b>
              </button>
            ))}
            <p className={styles.hygieneWhy}>
              These do not stop anyone working a record — they matter only to the bulk loader.
              They are kept out of the defect count so the real ones stay visible.
            </p>
          </div>
        )}

        {only && (
          <p className={styles.cost}>
            <b>What it costs:</b> {DEFECTS.find((d) => d.id === only)?.cost}
          </p>
        )}

        {/* ⚠️ THE REPAIR OPTIONS ARE SHOWN UP FRONT, NOT BEHIND A FILTER.
            Brian: "it shows me what is actually wrong but doesnt give me an
            option to fix the issues."
            It did offer one — but only AFTER you had clicked a specific defect
            chip, which nobody has a reason to do before knowing a fix exists.
            An action gated behind a step you would only take if you already
            knew the action was there is not an action anyone finds. */}
        {(() => {
          /* ⚠️ ONLY A FIX THE BACKEND CAN NAME. `repairRuleFor` returns null for
             anything that is not a repair-defects rule (the key backfill is
             bookkeeping and lives on the Integrity page), and a null scope is
             never sent: the route runs EVERY rule when `only` is blank. */
          const fixable = DEFECTS.filter((d) => counts.get(d.id) && repairRuleFor(d.fixable))
          const manual = DEFECTS.filter((d) => counts.get(d.id) && !d.fixable && !d.audience)
          if (!fixable.length && !manual.length) return null
          return (
            <div className={styles.fix}>
              <span className={styles.fixHead}>What can be repaired</span>

              {fixable.map((d) => (
                <div key={d.id} className={styles.fixOne}>
                  <p className={styles.fixWhat}>
                    <b>{d.fixLabel}</b> — {counts.get(d.id).toLocaleString()} record
                    {counts.get(d.id) === 1 ? '' : 's'}. It only fills what is blank and never
                    overwrites a value somebody already set.
                  </p>
                  <div className={styles.fixRow}>
                    <button type="button" className={styles.fixBtn} data-kind="dry" disabled={fix.busy}
                      onClick={async () => {
                        setFix({ busy: true, dry: null, applied: null, error: '', id: d.id })
                        try {
                          const rule = repairRuleFor(d.fixable)
                          if (!rule) throw new Error('No repair rule is named for this defect, so nothing was sent.')
                          /* Narrowed to THIS body's records. `ids` can only narrow a
                             rule, never widen it, and the button is labelled with this
                             body's count, so it must not touch another body's rows. */
                          /* ⚠️ THROUGH bodyControls, NOT api.repairDefects DIRECTLY.
                             This call used to send `ids.slice(0, 500)` and `limit: 300`
                             under a button labelled with the FULL defect count, so on a
                             900-record rule it silently did a third of the job and
                             reported success. `repairDefect()` sends the same caps and
                             returns the shortfall in a sentence, which is then rendered.
                             A control that lies about its own blast radius is a dead end
                             one step further on. */
                          const r = await repairDefect({ rule, ids: idsBy.get(d.id) || [], dryRun: true })
                          if (!r.ok) throw new Error(r.note)
                          setFix({ busy: false, dry: r, applied: null, error: '', id: d.id })
                        } catch (e) { setFix({ busy: false, dry: null, applied: null, error: e?.message || 'The repair could not be reached.', id: d.id }) }
                      }}>
                      {fix.busy && fix.id === d.id ? 'Checking…' : 'Show me what would change'}
                    </button>
                    {/* ⚠️ APPLY ONLY EXISTS AFTER A DRY RUN HAS REPORTED. A bulk
                        write across a live object must never be one click from a
                        list view. */}
                    {fix.dry && fix.id === d.id && (
                      <button type="button" className={styles.fixBtn} disabled={fix.busy}
                        onClick={async () => {
                          setFix((f) => ({ ...f, busy: true, error: '' }))
                          try {
                            const rule = repairRuleFor(d.fixable)
                            if (!rule) throw new Error('No repair rule is named for this defect, so nothing was sent.')
                            const r = await repairDefect({ rule, ids: idsBy.get(d.id) || [], dryRun: false })
                            if (!r.ok) throw new Error(r.note)
                            setFix({ busy: false, dry: null, applied: r, error: '', id: d.id })
                          } catch (e) { setFix((f) => ({ ...f, busy: false, error: e?.message || 'The repair failed.' })) }
                        }}>
                        Apply it
                      </button>
                    )}
                  </div>
                  {/* ⚠️ THE SENTENCE IS THE RUNNER'S, NOT THIS COMPONENT'S, and that
                      is the fix. The old text read the raw route response and said
                      "N records would change" — true of the CALL, silent about the
                      fact that the call covered at most 500 ids and wrote at most
                      300 of them while the button beside it was labelled with the
                      full count. `repairDefect()` composes one sentence that carries
                      the result AND the shortfall, so the two can never drift. */}
                  {fix.dry && fix.id === d.id && (
                    <p className={styles.fixResult}><b>Dry run:</b> {fix.dry.note}</p>
                  )}
                  {fix.applied && fix.id === d.id && (
                    <p className={styles.fixResult}><b>Done:</b> {fix.applied.note} Reload to see new counts.</p>
                  )}
                  {fix.error && fix.id === d.id && <p className={styles.fixResult}>{fix.error}</p>}
                </div>
              ))}

              {/* ⚠️ AND WHY THE REST HAVE NO BUTTON, said plainly. Silence here
                  reads as an oversight; the truth is that a machine picking
                  which duplicate survives, or inventing a date for an event
                  nobody scheduled, is how a bad record becomes the surviving
                  record. */}
              {manual.length > 0 && (
                <p className={styles.fixWhat}>
                  <b>{manual.map((d) => d.label).join(', ')}</b> {manual.length === 1 ? 'has' : 'have'} no
                  repair button on purpose — each needs a human call. Which of two duplicates survives, or
                  whether a blank date is unknown or simply has no event, is judgement. Open a row below to
                  work it on the record itself.
                </p>
              )}
            </div>
          )
        })()}

        <div className={styles.rows}>
          {rows.map(({ raw: r, ids }) => {
            const dec = decidedBy(r)
            return (
              <article key={r.id || r.hs_object_id} className={styles.row}
                role="button" tabIndex={0}
                onClick={() => setRecord(normalizeChampionship(r))}
                onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); setRecord(normalizeChampionship(r)) } }}>
                <div className={styles.rowMain}>
                  <span className={styles.name}>{r.championship_name || '(unnamed record)'}</span>
                  <span className={styles.meta}>
                    {[r.sr_sport, r.sr_level, r.sr_season_year].filter(Boolean).join(' · ') || 'no sport recorded'}
                  </span>
                </div>
                <div className={styles.flags}>
                  {ids.map((id) => (
                    <span key={id} className={styles.flag}
                      data-kind={id === 'standingsNoHunt' ? 'hunt' : 'bad'}>
                      {DEFECTS.find((d) => d.id === id)?.label || id}
                    </span>
                  ))}
                </div>
                {/* ⚠️ A STANDINGS TITLE IS NOT BROKEN — it explains itself here so
                    nobody "fixes" it by inventing a date for an event that does
                    not exist. */}
                {dec.how === 'standings' && (
                  <p className={styles.why}>{dec.reason}</p>
                )}
              </article>
            )
          })}
          {!rows.length && <p className={styles.none}>No records match this filter.</p>}
        </div>

        {total > rows.length && (
          <p className={styles.more}>
            Showing {rows.length.toLocaleString()} of {total.toLocaleString()} — filter by a defect to narrow.
          </p>
        )}
      </div>

      {/* The same read-only drawer the Command Center opens, so a defect is
          corrected in one place rather than two divergent ones. */}
      {record && (
        <ChampionshipDrawer champ={record} now={Date.now()} onClose={() => setRecord(null)} />
      )}
    </div>,
    document.body,
  )
}
